<template>
  <div class="image-field" v-bind:class="{ 'full': isFull }" :style="{ width, height }">
    <div v-if="editable" class="image-field__dropzone">
      <div class="image-field__prompt dz-message">
        <span class="ss-icon">upload</span>
        <span class="image-field__message">{{ currentMessage }}</span>
        <span v-if="recommendations" class="image-field__recommendations">{{ recommendations }}</span>
      </div>

      <input v-bind:class="{ 'val-required': isRequired }" :id="inputId" :name="paramName" @change="readFile" type="file" accept="image/*" class="image-field__file" :required="isRequired" ref="fileSelect"></input>
      <input class="image-field__remove_file" type="hidden" :name="removeParamName()" />
    </div>

    <div>
      <img v-show="currentValue && !showDropzone" ref="img" :src="currentValue" class="image-field__preview" />
    </div>

    <div v-if="showControls" class="image-field__controls">
      <button v-if="editable" @click.prevent="toggleDropzone" class="image-field__edit ss-icon">upload</button>
      <button v-if="editable && removable" @click.prevent="removeFile" class="image-field__remove ss-icon">delete</button>
    </div>

    <slot></slot>
  </div>
</template>

<script>
  import FormOptions from './mixins/form-options'

  export default {
    mixins: [FormOptions],

    props: {
      editable: {
        default: true
      },
      removable: {
        default: true
      },
      resizable: {
        default: false
      },
      width: String,
      height: String,
      message: {
        default: null
      },
      recommendations: {
        default: null
      },
      required: {
        default: false
      }
    },

    data () {
      return {
        showDropzone: false,
        hasValue: !!this.value,
        currentValue: this.value,
        currentMessage: this.message,
        isFull: false,
        isRequired: this.required
      }
    },

    mounted () {
      if (typeof I18n != 'undefined') {
        this.currentMessage = I18n.t('drop_a_file_here_or_click_to_browse')
      } else {
        this.currentMessage = 'Drop a file here or click to browse'
      }

      if(this.resizable == false) {
        this.isFull = false
      } else if (this.hasValue) {
        this.isFull = true
      } else {
        this.isFull = false
      }

      if(this.hasValue) {
        this.isRequired = false
      }
    },

    computed: {
      imageFilename () {
        if (this.currentValue && !this.currentValue.match(/^data:.+/)) return this.currentValue.split("/").pop()
      },

      showControls () {
        return this.currentValue && this.hasValue
      },

      attributeHuman () {
        return this.attribute.split(',')[0]
      }
    },

    methods: {
      toggleDropzone() {
        this.showDropzone = !this.showDropzone
        this.$refs.fileSelect.click()

        if (this.removable) {
          this.removeFile()
        }

        if (this.resizable) {
          this.isFull = false
        }

        if (this.required) {
          this.isRequired = true
        }
      },

      readFile (event) {
        const files = event.target.files

        if (files && files[0]) {
          if (this.allowedType(files[0])) {
            const reader = new FileReader()

            reader.onload = (e) => {
              const imageUrl = e.target.result

              this.$refs.img.setAttribute('src', imageUrl)
              this.updateValue(imageUrl)
            }

            reader.readAsDataURL(files[0])
            
            this.$el.querySelector('.image-field__remove_file').value = '';

            this.$emit('readFile', this.paramName, files[0])
          } else {
            if (typeof I18n != 'undefined') {
              this.currentMessage = 'Please use a JPG, PNG, or GIF'
            } else {
              this.currentMessage = I18n.t('please_use_a_jpg_png_or_gif')
            }
          }
        }

        if(this.resizable) {
          this.isFull = true
        }
      },

      removeFile () {
        this.$el.querySelector('input[type="file"]').value = '';
        this.$el.querySelector('.image-field__remove_file').value = '1';
        this.updateValue(null);
      },
      
      removeParamName () {
        return this.paramName.replace(/\[(?!.*\[)/, '[remove_');
      },

      updateValue (value) {
        this.hasValue = !!value
        this.showDropzone = !value
        this.currentValue = value
        this.$emit('input', value)
      },

      allowedType (file) {
        const allowedTypes = ['jpg', 'jpeg', 'png', 'gif']
        const extension = file.name.split('.').pop().toLowerCase()

        return allowedTypes.indexOf(extension) > -1
      }
    }
  }
</script>

<style scoped lang="scss">
  @import 'edit-button';

  .image-field {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 100%;
  }

  .image-field__preview,
  .image-field__dropzone,
  .image-field__file {
    min-width: 100%;
    min-height: 100%;
    object-fit: cover;
    position: absolute;
    margin: auto;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
  }

  .image-field__dropzone {
    background-color: var(--bg-2);
    color: var(--fg-2);
    cursor: pointer;
    font-size: $fs_large_body;
    text-align: center;
    height: 100%;
    width: 100%;
  }

  .image-field__prompt {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    height: 100%;
    width: 100%;
  }

  .image-field__recommendations {
    margin-top: 6px;
    font-size: 12px;
    @include italic;
  }

  .image-field__file {
    opacity: 0;
    cursor: pointer;
  }

  .image-field__controls {
    display: flex;
    flex-direction: row;
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }

  .image-field__edit,
  .image-field__remove {
    @extend .edit-button;
    background: #fff;
    color: #000;
    margin-bottom: 0.5rem;
    padding-right: 2px;
  }

  // special case for gif upload
  .company-gif {

    .image-field {

      &__preview {
        min-height: 0;
        position: relative;
      }

      &__file {
        min-width: 0;
        min-height: 0;
      }
    }
  }

  // special case for company logo
  .company-logo .image-field__preview {
    object-fit: contain;
    background-color: var(--bg);
  }

  .full {
    height: auto !important;
  }
</style>



// WEBPACK FOOTER //
// app/javascript/components/image-field.vue?4e4f8203